import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Box,
  Chip,
  Avatar,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Typography,
  IconButton,
  Snackbar,
} from '@material-ui/core'
import { Alert, Pagination } from '@material-ui/lab'
import Tooltip from '@material-ui/core/Tooltip'
import Tab from '@material-ui/core/Tab'
import Tabs from '@material-ui/core/Tabs'
import DeleteIcon from '@material-ui/icons/Delete'
import RefreshIcon from '@material-ui/icons/Refresh'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import { DropzoneArea } from 'material-ui-dropzone'
import { FileTransferHelper } from '../../../helpers/file-transfer-helper'
import { MediaHelper } from '../../../helpers/media-helper'
import { KeycloakHelper, useDebounce, http } from '@monorepo/infra'
import axios from 'axios'
import CreatableSelect from 'react-select/creatable'
import * as S from './styles'

interface MediaItem {
  _id: string
  path: string
  name: string
  mimetype: string
  size: number
  tags: string[]
  email: string
  mediaType: string
  permissions: string[]
  isInUse: boolean
  usageCount: number
  usageDetails: {
    entity: string
    entityId: string
    variantIndex: number
  }[]
}

interface MediaSelectorProps {
  open: boolean
  onClose: () => void
  onSelect: (mediaItems: MediaItem[]) => void
  mediaType: 'thumb' | 'inDesign' | 'download' | 'importProduct'
  currentMedia: string[]
  multiple?: boolean
  keycloak?: unknown
  hideActions?: boolean
}

const MediaSelector: React.FC<MediaSelectorProps> = ({
  open,
  onClose,
  onSelect,
  mediaType,
  currentMedia,
  multiple = true,
  keycloak,
  hideActions = false,
}) => {
  const [mediaList, setMediaList] = useState<MediaItem[]>([])
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearchTerm = useDebounce(searchTerm, 400)
  const [selectedItems, setSelectedItems] = useState<MediaItem[]>([])
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
  })
  const [filterByCurrentUser, setFilterByCurrentUser] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [mediaToDelete, setMediaToDelete] = useState<MediaItem | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>(
    'success'
  )
  const [uploading, setUploading] = useState(false)
  const [dropzoneKey, setDropzoneKey] = useState(0)
  const [tags, setTags] = useState<string[]>([])
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({})

  const fetchMediaList = useCallback(async () => {
    setLoading(true)
    setFetchError(false)
    try {
      const currentUserEmail = keycloak
        ? KeycloakHelper.getCurrentUserEmail(keycloak)
        : undefined

      const params = {
        page: pagination.page,
        pageSize: pagination.pageSize,
        mediaType: mediaType,
        search: debouncedSearchTerm || undefined,
        email: filterByCurrentUser ? currentUserEmail : undefined,
      }

      const response = await MediaHelper.getMediaList(params, keycloak)
      setMediaList(response.data)
      setPagination(response.pagination)
    } catch (error) {
      console.error('Error fetching media list:', error)
      setFetchError(true)
    } finally {
      setLoading(false)
    }
  }, [
    pagination.page,
    pagination.pageSize,
    debouncedSearchTerm,
    mediaType,
    filterByCurrentUser,
    keycloak,
  ])

  useEffect(() => {
    if (open) {
      setFetchError(false)
      fetchMediaList()
    }
  }, [open, fetchMediaList])

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
    // Reset to first page when search term changes
    setPagination((prev) => ({ ...prev, page: 1 }))
  }

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setPagination((prev) => ({ ...prev, page }))
  }

  const handleItemSelect = useCallback(
    (item: MediaItem) => {
      if (multiple) {
        setSelectedItems((prev) => {
          const isSelected = prev.some((selected) => selected._id === item._id)
          if (isSelected) {
            return prev.filter((selected) => selected._id !== item._id)
          } else {
            return [...prev, item]
          }
        })
      } else {
        // in mediaType [inDesign, download] clicking on same row won't unselect the item
        // setSelectedItems([item])

        // Toggle behavior for single-select: clicking the same item unselects it
        setSelectedItems((prev) => {
          const isSelected = prev.length === 1 && prev[0]._id === item._id
          return isSelected ? [] : [item]
        })
      }
    },
    [multiple]
  )

  const handleConfirmSelection = () => {
    onSelect(selectedItems)
    setSelectedItems([])
    onClose()
  }

  const handleClose = () => {
    setSelectedItems([])
    setSearchTerm('')
    setTags([])
    setPendingFiles([])
    onClose()
  }

  const isItemSelected = useCallback(
    (item: MediaItem) => {
      return selectedItems.some((selected) => selected._id === item._id)
    },
    [selectedItems]
  )

  const isItemAlreadyUsed = (item: MediaItem) => {
    return currentMedia.includes(item.path)
  }

  const handleDeleteMedia = (item: MediaItem) => {
    setMediaToDelete(item)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!mediaToDelete) return

    setDeleteLoading(true)
    try {
      await MediaHelper.deleteMedia(mediaToDelete._id, keycloak)

      // Remove the deleted item from the current list
      setMediaList((prev) =>
        prev.filter((item) => item._id !== mediaToDelete._id)
      )

      // Update pagination count
      setPagination((prev) => ({
        ...prev,
        totalCount: prev.totalCount - 1,
      }))

      setSnackbarMessage(`Media "${mediaToDelete.name}" deleted successfully`)
      setSnackbarSeverity('success')
      setSnackbarOpen(true)

      setDeleteDialogOpen(false)
      setMediaToDelete(null)
    } catch (error) {
      console.error('Error deleting media:', error)
      setSnackbarMessage('Failed to delete. Media is in use.')
      setDeleteDialogOpen(false)
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    } finally {
      setDeleteLoading(false)
      setDeleteDialogOpen(false)
    }
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false)
    setMediaToDelete(null)
  }

  const getFileIcon = (identifier: string) => {
    const value = identifier?.toLowerCase() || ''
    if (
      value.startsWith('image/') ||
      /\.(png|jpe?g|gif|bmp|webp|svg)$/.test(value)
    ) {
      return '🖼️'
    } else if (value.includes('zip') || value.endsWith('.zip')) {
      return '📦'
    } else if (
      value.endsWith('sheet') ||
      value.endsWith('.xlsx') ||
      value.endsWith('.xls')
    ) {
      return '📥'
    } else {
      return '📄'
    }
  }

  useEffect(() => {
    const urls: Record<string, string> = {}
    pendingFiles.forEach((file) => {
      const key = `${file.name}-${file.lastModified}`
      if (file.type.startsWith('image/')) {
        urls[key] = URL.createObjectURL(file)
      }
    })
    setPreviewUrls(urls)

    return () => {
      Object.values(urls).forEach((url) => URL.revokeObjectURL(url))
    }
  }, [pendingFiles])

  const handleUploadFiles = useCallback(async () => {
    if (pendingFiles.length === 0 || uploading) return

    setUploading(true)
    try {
      const user = keycloak ? KeycloakHelper.getTokenParsed(keycloak) : undefined

      for (const file of pendingFiles) {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('isInDesign', mediaType === 'inDesign' ? 'true' : 'false')
        formData.append('tags', tags.join(','))
        formData.append('email', user?.email)
        formData.append('mediaType', mediaType)
        await http.post({
          url: `${process.env.REACT_APP_MEDIA_URL}`,
          data: formData,
          headers: {
            'content-type': 'multipart/form-data',
            Authorization: `Bearer ${KeycloakHelper.getToken(keycloak)}`,
          },
        })
      }

      setUploading(false)
      setActiveTab(0)
      setSnackbarMessage('Upload successful')
      setSnackbarSeverity('success')
      setSnackbarOpen(true)
      setTags([])
      setPendingFiles([])
      setDropzoneKey((k) => k + 1)
      await fetchMediaList()
    } catch (e) {
      setSnackbarMessage('Upload failed')
      setSnackbarSeverity('error')
      setSnackbarOpen(true)
    } finally {
      setUploading(false)
    }
  }, [
    pendingFiles,
    uploading,
    keycloak,
    mediaType,
    tags,
    fetchMediaList,
  ])

  const handleRemovePendingFile = useCallback((keyToRemove: string) => {
    setPendingFiles((prev) =>
      prev.filter((file) => `${file.name}-${file.lastModified}` !== keyToRemove)
    )
  }, [])

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth style={{ zIndex: 1400 }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
            Select{' '}
            {mediaType === 'thumb'
              ? 'Images'
              : mediaType === 'inDesign'
                ? 'InDesign File'
                : mediaType === 'download'
                  ? 'Download File'
                  : 'Import Product File'}
          </Typography>
          <Tabs value={activeTab} onChange={(e, value) => setActiveTab(value)}>
            <Tab label="Browse Existing" />
            <Tab label="Upload New" />
          </Tabs>
        </Box>
      </DialogTitle>

      <DialogContent>
        {activeTab === 0 ? (
          <>
            <Box style={{ marginBottom: 16, display: 'flex', alignItems: 'center', paddingTop: 4, gap: 8 }}>
              <TextField
                label="Search files"
                placeholder="Search files by name or email"
                value={searchTerm}
                onChange={handleSearch}
                variant="outlined"
                style={{ width: '50%' }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filterByCurrentUser}
                    onChange={(e) => setFilterByCurrentUser(e.target.checked)}
                  />
                }
                label="My uploads only"
              // style={{ width: '100%' }}
              />
            </Box>

            {loading ? (
              <Box style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
                <CircularProgress />
              </Box>
            ) : fetchError ? (
              <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: 16 }}>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Failed to load media files
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  There was an error loading the media list. Please try again.
                </Typography>
                <IconButton
                  onClick={fetchMediaList}
                  color="primary"
                  style={{ marginTop: 8 }}
                >
                  <RefreshIcon />
                </IconButton>
              </Box>
            ) : (
              <TableContainer component={Paper} style={{ maxWidth: '100%', overflowX: 'auto' }}>
                <Table style={{ minWidth: 650 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell style={{ width: 80 }}>Select</TableCell>
                      <TableCell style={{ width: 200, minWidth: 150, wordBreak: 'break-word' }}>ID</TableCell>
                      <TableCell style={{ width: 80 }}>Preview</TableCell>
                      <TableCell style={{ minWidth: 200, maxWidth: 200 }}>
                        Name
                      </TableCell>
                      <TableCell style={{ width: 100 }}>Size</TableCell>
                      <TableCell style={{ minWidth: 110 }}>Tags</TableCell>
                      <TableCell style={{ minWidth: 140 }}>Uploaded By</TableCell>
                      <TableCell style={{ width: 100 }}>Type</TableCell>
                      {!hideActions && <TableCell style={{ width: 80 }}>Actions</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mediaList.map((item) => (
                      <TableRow
                        key={item._id}
                        hover
                        onClick={(e) => {
                          // Don't trigger row selection if checkbox was clicked
                          if (
                            e.target instanceof HTMLInputElement &&
                            e.target.type === 'checkbox'
                          ) {
                            return
                          }
                          handleItemSelect(item)
                        }}
                        style={{ cursor: 'pointer' }}
                        selected={isItemSelected(item)}
                      >
                        <TableCell style={{ width: 80 }}>
                          <Checkbox
                            checked={isItemSelected(item)}
                            onChange={(e) => {
                              e.stopPropagation()
                              handleItemSelect(item)
                            }}
                          // disabled={isItemAlreadyUsed(item)}
                          />
                          {/* {isItemAlreadyUsed(item) && (
                            <Typography variant="caption" color="text.secondary">
                              Already used
                            </Typography>
                          )} */}
                        </TableCell>
                        <TableCell style={{ width: 200, minWidth: 150, wordBreak: 'break-word' }}>
                          <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                            <Tooltip title="Copy ID">
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (item._id) {
                                    navigator.clipboard.writeText(item._id)
                                    setSnackbarMessage('Copied ID')
                                    setSnackbarSeverity('success')
                                    setSnackbarOpen(true)
                                  }
                                }}
                              >
                                <FileCopyIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                              {item._id ? item._id : ''}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell style={{ width: 80 }}>
                          {item.mimetype.startsWith('image/') ? (
                            <Avatar
                              src={item.path}
                              variant="rounded"
                              style={{ width: 50, height: 50 }}
                              imgProps={{ loading: 'lazy' }}
                            />
                          ) : (
                            <Avatar style={{ width: 50, height: 50 }}>
                              {getFileIcon(item.mimetype)}
                            </Avatar>
                          )}
                        </TableCell>
                        <TableCell style={{ minWidth: 200, maxWidth: 200, wordWrap: 'break-word' }}>
                          <Typography
                            variant="body2"
                            style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}
                          >
                            {mediaType !== 'inDesign' ? item.name.split('@')[1] : item.name}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ width: 100 }}>
                          {FileTransferHelper.formatBytes(item.size)}
                        </TableCell>
                        <TableCell style={{ minWidth: 110 }}>
                          <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {item.tags.map((tag, index) => (
                              tag.length > 0 ? (
                                <Chip key={index} label={tag} size="small" />
                              ) : null
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell style={{ minWidth: 140 }}>
                          {item.email}
                        </TableCell>
                        <TableCell style={{ width: 100 }}>
                          {item.mediaType}
                        </TableCell>

                        {!hideActions && (
                          <TableCell style={{ width: 80 }}>
                            <Tooltip
                              title={
                                item.isInUse
                                  ? 'Cannot delete - Media is in use'
                                  : 'Delete media'
                              }
                            >
                              <span>
                                <IconButton
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleDeleteMedia(item)
                                  }}
                                  // disabled={item.isInUse}
                                  color={item.isInUse ? 'default' : 'secondary'}
                                  size="small"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {pagination.totalPages > 1 && (
              <Box display="flex" justifyContent="center" mt={2}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.page}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        ) : (
          <Box p={2} display="flex" flexDirection="column" alignItems="center">
            <Box width="100%" maxWidth={600} minWidth={500}>
              <Typography variant="h6" gutterBottom style={{ textAlign: 'center', fontWeight: 'bold' }}>
                Upload New{' '}
                {mediaType === 'thumb'
                  ? 'Image'
                  : mediaType === 'inDesign'
                    ? 'InDesign File'
                    : mediaType === 'importProduct'
                      ? 'Import Product File'
                      : mediaType === 'download'
                        ? 'Download File'
                        : ''}
              </Typography>

              <Box mb={2} width="100%">
                <Typography variant="body1" style={{ display: 'block', marginBottom: 8, fontWeight: 'bold' }}>
                  Tags
                </Typography>
                <CreatableSelect
                  isMulti
                  placeholder="DIRECTV, AT&T"
                  value={tags.map((t) => ({ value: t, label: t }))}
                  components={{
                    IndicatorSeparator: () => null,
                    DropdownIndicator: () => null,
                  }}
                  onChange={(newValue) => setTags(newValue.map((opt) => opt.value))}
                  styles={S.MultiSelectStyle}
                />
              </Box>

              <Box width="100%">
                <DropzoneArea
                  key={dropzoneKey}
                  dropzoneText={`Drag & drop ${mediaType === 'thumb'
                      ? 'images'
                      : mediaType === 'inDesign'
                        ? 'zip files'
                        : mediaType === 'importProduct'
                          ? 'Excel files'
                          : mediaType === 'download'
                            ? 'files'
                            : 'a file'
                    } here or click`}
                  showAlerts={['error']}
                  showPreviewsInDropzone={false}
                  // showPreviews
                  // useChipsForPreview
                  filesLimit={1}
                  maxFileSize={100000000}
                  acceptedFiles={
                    mediaType === 'thumb'
                      ? ['image/jpeg', 'image/png', 'image/bmp']
                      : mediaType === 'inDesign'
                        ? [
                          'application/zip',
                          'application/x-zip-compressed',
                          'multipart/x-zip',
                          'application/octet-stream',
                        ]
                        : mediaType === 'importProduct'
                          ? ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet']
                          : []
                  }
                  onChange={(selected) => {
                    if (uploading || selected.length === 0) return
                    const files =
                      mediaType === 'thumb'
                        ? selected
                        : [selected[selected.length - 1]]
                    setPendingFiles(files)
                  }}
                />
                
                {pendingFiles.length > 0 && (
                  <Box mt={3}>
                    <Typography
                      variant="subtitle1"
                      style={{ fontWeight: 'bold', marginBottom: 8 }}
                    >
                      Selected {pendingFiles.length > 1 ? 'Files' : 'File'}
                    </Typography>
                    <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
                      {pendingFiles.map((file) => {
                        const key = `${file.name}-${file.lastModified}`
                        const isImage = file.type.startsWith('image/')
                        return (
                          <Paper
                            key={key}
                            style={{
                              padding: 12,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 16,
                            }}
                          >
                            <Box>
                              <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                                <Avatar
                                  variant="rounded"
                                  style={{ width: 56, height: 56 }}
                                  src={isImage ? previewUrls[key] : undefined}
                                >
                                  {!isImage && getFileIcon(file.type || file.name)}
                                </Avatar>
                                <Box>
                                  <Typography variant="body1" style={{ fontWeight: 500 }}>
                                    {file.name}
                                  </Typography>
                                  <Typography variant="body2" color="textSecondary">
                                    {FileTransferHelper.formatBytes(file.size)}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                            <Box display="flex" alignItems="center" justifyContent="center" style={{ gap: 8 }}>
                            {uploading && (
                              <Box display="flex" justifyContent="center">
                                <CircularProgress size={20} color="secondary" />
                              </Box>
                            )}                            
                              <IconButton
                              onClick={() => handleRemovePendingFile(key)}
                              color="secondary"
                              size="small"
                              >
                                <DeleteIcon />
                            </IconButton>
                            </Box>
                          </Paper>
                        )
                      })}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant='outlined'>Cancel</Button>
        {activeTab === 1 && (
          <Button
            onClick={handleUploadFiles}
            variant="contained"
            color="primary"
            disabled={pendingFiles.length === 0 || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        )}
        {activeTab === 0 && (
          <Button
            onClick={handleConfirmSelection}
            variant="contained"
            color="primary"
            disabled={selectedItems.length === 0}
          >
            Select {selectedItems.length > 0 ? `(${selectedItems.length})` : ''}
          </Button>
        )}
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        style={{ zIndex: 1500 }}
      >
        <DialogTitle id="delete-dialog-title" style={{ fontWeight: 'bold' }}>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography id="delete-dialog-description">
            Are you sure you want to delete the media "{mediaType !== 'inDesign' ? mediaToDelete?.name.split('@')[1] : mediaToDelete?.name}"?
            {<br />}
            {<br />}
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} style={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  )
}

export default MediaSelector
